import { Alert, AlertDescription, AlertTitle } from '@invent/wl-ui-kit-next/ui';

import { useAuth, useCan } from '@/domains/auth';
import { accountsApi } from '../../api/accounts-api';
import type { IAdminAccountDetail } from '../../api/account-contracts';
import { TableSkeleton, useAdminData } from '../../components/admin-page';
import { AccountRolesCard } from '../../components/account-roles';
import { SignInCard, accountPowers } from '../../components/sign-in-card';

/**
 * A hold on one account of the household, inside Account & access (SR-215, R13), for a viewer
 * holding `accounts.browse`: how it signs in and the Team Portal roles — the user card's Sign-in
 * card (SR-476) and its roles card, drawn by the same components, each on its own permission.
 *
 * **Its own read, because the hub's document stops short.** The participant card carries no
 * `idp`, so how the account signs in is composed only from `GET …/accounts/by-email/{email}`,
 * read here per account for a viewer holding `accounts.browse` and never for one without. The
 * alternative, composing it from the hub's account, would read an absent `idp` as "not linked":
 * the one fact the hub's document withholds. The read has the admin screens' arms — a skeleton,
 * its failure, and "no such account" for a 404 alone — and keeps what it last read when a re-read
 * fails, saying so.
 *
 * **A write re-reads both.** `reread` is the page's re-read after a write, which bumps `written`,
 * and `written` is in this read's dependencies — so a roles change or an unlink re-reads the hub
 * (the header's chips, the account's facts, the activity) and this account in one step, as the
 * user card re-reads its one read. An invitation sent re-reads its own list alone: its event is
 * the worker's, written when the IdP has made it.
 */
export function AccountCredentials({
  email,
  written,
  reread
}: {
  email: string;
  written: number;
  reread: () => Promise<boolean>;
}) {
  const { user } = useAuth();
  const can = useCan();
  // Wrapped in an object for the user card's reason: `null` is "not loaded yet" to
  // `useAdminData`, and a bare `null` for "no such account" would sit on a skeleton forever.
  const { data, error } = useAdminData<{ account: Nullable<IAdminAccountDetail> }>(
    async (signal) => ({ account: await accountsApi.getAccountByEmail(email, signal) }),
    [email, written]
  );
  const account = data?.account ?? null;
  const powers = accountPowers(can, user?.email, account);

  if (data === null) {
    return error ? (
      <Alert variant="destructive">
        <AlertTitle>Could not read how this account signs in</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    ) : (
      <TableSkeleton rows={3} />
    );
  }

  if (!account) {
    return (
      <Alert>
        <AlertTitle>No such account</AlertTitle>
        <AlertDescription>
          Nothing is registered at <strong>{email}</strong> any more — the page was read before it
          went.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    // The kit's buttons never wrap, and two cards deep in a section a phone is narrower than a
    // long label: one button widened every section of the page past the screen. So a button's
    // label wraps here, and the address the cards' copy spells unbroken may break anywhere.
    <div className="grid min-w-0 gap-4 [overflow-wrap:anywhere] [&_[data-slot=button]]:h-auto [&_[data-slot=button]]:min-h-9 [&_[data-slot=button]]:whitespace-normal">
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not refresh — showing the account as last read</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <SignInCard account={account} powers={powers} onReread={() => void reread()} />
      <AccountRolesCard
        account={account}
        isSelf={powers.isSelf}
        canGrant={powers.canGrant}
        onChanged={reread}
      />
    </div>
  );
}
